/**
 * Returns true if the name is a valid Backstage entity name
 * (^[a-zA-Z0-9][a-zA-Z0-9._-]*$, max 63 chars).
 */
function isValidEntityName(name: string): boolean {
  return name.length <= 63 && /^[a-zA-Z0-9][a-zA-Z0-9._-]*$/.test(name);
}

/**
 * Extracts a Backstage-entity-name-compatible token from a security-insights
 * asset string, or returns null if no valid name can be derived.
 *
 * Assets come from many sources — GitHub repos (`<org>/<repo>`), cloud
 * resources (EC2 instance IDs, hostnames, ARN tails, etc.), and bare names.
 * We strip a leading `<org>/` segment (Backstage's default GitHub ingestion
 * uses just the repo as the entity name), then validate the result against
 * Backstage's entity-name rules.
 *
 * Shapes we can't usefully turn into a name (multi-segment paths, ARNs,
 * docker image refs with tags, anything with characters outside the entity-
 * name charset) return null.
 */
function assetToEntityName(asset: string): string | null {
  if (!asset) return null;
  if (asset.startsWith('arn:')) return null;
  const slashIdx = asset.indexOf('/');
  const candidate = slashIdx === -1 ? asset : asset.slice(slashIdx + 1);
  if (candidate.includes('/')) return null;
  if (!isValidEntityName(candidate)) return null;
  return candidate;
}

/**
 * GHAS issue types (GitHub Advanced Security findings). Assets for these
 * findings are always GitHub repos registered as `Component` entities — so
 * we skip lookups for other kinds.
 *
 * Source: `SELECT DISTINCT issue_type FROM security_findings_union WHERE
 * tool LIKE 'github%'` against the security pipeline DB.
 */
const GHAS_ISSUE_TYPES: ReadonlySet<string> = new Set([
  'CODEQL',
  'DEPENDABOT',
  'TRIVY',
  'MOBSFSCAN',
  'SONARQUBE',
  'DEFSEC',
  'ZAP MEDIUM ISSUE',
]);

const NON_GITHUB_KINDS: ReadonlyArray<string> = ['resource', 'api', 'system'];
const ALL_ASSET_KINDS: ReadonlyArray<string> = ['component', ...NON_GITHUB_KINDS];

/**
 * Builds candidate entity refs for an asset string. The caller submits all
 * candidates to the existence tracker and links to whichever one the
 * catalog confirms exists. Returns an empty array if no plausible entity
 * name can be derived.
 *
 * When `issueType` is provided we narrow the candidate kinds based on the
 * source — GHAS findings (CodeQL, Dependabot, Trivy, etc.) always target
 * GitHub repos which are catalogued as `Component`, so for those we skip
 * the `resource` / `api` / `system` lookups. Non-GHAS issue types describe
 * cloud / SaaS assets that aren't catalogued as `Component`, so we skip
 * that kind instead. With no `issueType` (e.g. the by-asset roll-up view)
 * we fall back to checking all kinds.
 *
 * Candidates are returned in priority order — if multiple kinds happen to
 * share a name in the catalog, the first match wins.
 */
export function assetCandidateRefs(
  asset: string,
  issueType?: string,
): string[] {
  const name = assetToEntityName(asset);
  if (!name) return [];
  let kinds: ReadonlyArray<string>;
  if (!issueType) {
    kinds = ALL_ASSET_KINDS;
  } else if (GHAS_ISSUE_TYPES.has(issueType)) {
    kinds = ['component'];
  } else {
    kinds = NON_GITHUB_KINDS;
  }
  return kinds.map(kind => `${kind}:default/${name}`);
}

/**
 * Builds a candidate `group:default/<name>` ref for a security-insights team
 * string, or returns null if the value can't be a valid entity name.
 *
 * A non-null return is only a *candidate* — callers must verify existence
 * before rendering a link, so synthetic sentinels like `unowned` (no real
 * Group entity in the catalog) don't generate dead links.
 */
export function toGroupRef(team: string): string | null {
  if (!team || !isValidEntityName(team)) return null;
  return `group:default/${team}`;
}

/**
 * Returns the first candidate ref that exists in the catalog, or null.
 *
 * Centralises the "given a list of candidates, pick the live one" check
 * shared by column renderers and the summary table's `renderNameCell`.
 */
export function pickExistingRef(
  candidates: ReadonlyArray<string>,
  existing: ReadonlySet<string>,
): string | null {
  for (const ref of candidates) {
    if (existing.has(ref)) return ref;
  }
  return null;
}
